import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaBullhorn, FaComments, FaUser } from "react-icons/fa";
import "./BottomNav.css";

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Load unread count from localStorage or default to 5
  const [unreadCount, setUnreadCount] = useState(
    parseInt(localStorage.getItem("unreadCount")) || 5
  );

  // State for total unread messages count
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);

  // Save to localStorage whenever unreadCount changes
  useEffect(() => {
    localStorage.setItem("unreadCount", unreadCount);
  }, [unreadCount]);

  // Function to calculate total unread messages from conversations
  const calculateTotalUnreadMessages = () => {
    try {
      // Method 1: Check localStorage for conversations
      const savedConversations = localStorage.getItem("chat_conversations");
      if (savedConversations) {
        const conversations = JSON.parse(savedConversations);
        const totalUnread = conversations.reduce((total, conv) => {
          return total + (conv.unread_count || 0);
        }, 0);
        console.log("📊 Total unread messages from localStorage:", totalUnread);
        return totalUnread;
      }

      // Method 2: Check if there's a global unread count in localStorage
      const globalUnread = localStorage.getItem("global_unread_count");
      if (globalUnread) {
        console.log("📊 Total unread messages from global count:", globalUnread);
        return parseInt(globalUnread);
      }

      // Method 3: Check for individual conversation unread counts
      let totalFromStorage = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('unread_count_')) {
          const count = parseInt(localStorage.getItem(key));
          if (!isNaN(count)) {
            totalFromStorage += count;
          }
        }
      }

      return totalFromStorage;
    } catch (error) {
      console.error("Error calculating unread messages:", error);
      return 0;
    }
  };

  // Function to update unread count from real-time events
  const updateUnreadCount = () => {
    const count = calculateTotalUnreadMessages();
    setTotalUnreadMessages(count);
    console.log("🔄 Updated total unread messages:", count);
  };

  // Load unread messages count and set up listeners
  useEffect(() => {
    // Load initially
    updateUnreadCount();

    // Custom event listeners for chat updates
    const handleChatUpdate = () => {
      console.log("🔄 Received chat update event");
      setTimeout(updateUnreadCount, 100); // Small delay to ensure storage is updated
    };

    const handleNewMessage = (event) => {
      console.log("💬 Received new message event:", event);
      setTimeout(updateUnreadCount, 100);
    };

    const handleMessagesRead = () => {
      console.log("📖 Messages were read, updating count");
      setTimeout(updateUnreadCount, 100);
    };

    // Listen for storage changes (from other tabs/windows or same tab)
    const handleStorageChange = (e) => {
      if (e.key === "chat_conversations" || 
          e.key === "global_unread_count" ||
          (e.key && e.key.startsWith('unread_count_')) ||
          e.key === null) { // null means entire storage changed
        console.log("💾 Storage changed, updating unread count:", e.key);
        setTimeout(updateUnreadCount, 100);
      }
    };

    // Listen for focus events (when user returns to the tab)
    const handleFocus = () => {
      console.log("👀 Tab focused, checking for updates");
      updateUnreadCount();
    };

    // Add event listeners
    window.addEventListener("chatUpdated", handleChatUpdate);
    window.addEventListener("newMessageReceived", handleNewMessage);
    window.addEventListener("messagesMarkedAsRead", handleMessagesRead);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleFocus);
    
    // Custom events for real-time updates
    window.addEventListener("unreadCountUpdated", handleChatUpdate);
    window.addEventListener("conversationUpdated", handleChatUpdate);

    // Set up interval to check for updates (fallback - every 3 seconds)
    const interval = setInterval(updateUnreadCount, 3000);

    // Initial check after a short delay to ensure everything is loaded
    const initialCheck = setTimeout(updateUnreadCount, 1000);

    return () => {
      window.removeEventListener("chatUpdated", handleChatUpdate);
      window.removeEventListener("newMessageReceived", handleNewMessage);
      window.removeEventListener("messagesMarkedAsRead", handleMessagesRead);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("unreadCountUpdated", handleChatUpdate);
      window.removeEventListener("conversationUpdated", handleChatUpdate);
      clearInterval(interval);
      clearTimeout(initialCheck);
    };
  }, []);

  // Helper function to check if a path is active
  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);

    // If navigating to announcements, reset unread count to 0
    if (path === "/announcements") {
      setUnreadCount(0);
    }

    // If navigating to messages, trigger an update after navigation
    if (path === "/messages") {
      console.log("📱 Navigating to messages, will update counter");
      // Small delay to ensure chat component is loaded
      setTimeout(updateUnreadCount, 500);
    }
  };

  // Function to manually trigger update (can be called from other components)
  const triggerUpdate = () => {
    updateUnreadCount();
  };

  // Expose function globally for other components to call
  useEffect(() => {
    window.updateBottomNavUnreadCount = triggerUpdate;
    return () => {
      delete window.updateBottomNavUnreadCount;
    };
  }, []);

  return (
    <nav className="bottom-nav">
      <div
        className={`nav-item ${isActive("/announcements") ? "active" : ""}`}
        onClick={() => handleNavigation("/announcements")}
      >
        <div className="icon-wrapper">
          <FaBullhorn />
          {unreadCount > 0 && <span className="badge announcement-badge">{unreadCount}</span>}
        </div>
        <p>Announcement</p>
      </div>

      <div
        className={`nav-item ${isActive("/messages") ? "active" : ""}`}
        onClick={() => handleNavigation("/messages")}
      >
        <div className="icon-wrapper">
          <FaComments />
          {totalUnreadMessages > 0 && (
            <span className="badge message-badge">
              {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
            </span>
          )}
        </div>
        <p>Messages</p>
      </div>

      <div
        className={`nav-item ${isActive("/profile") ? "active" : ""}`}
        onClick={() => handleNavigation("/profile")}
      >
        <FaUser />
        <p>Profile</p>
      </div>
    </nav>
  );
};

export default BottomNav;